// Training participants + Certificates of Attendance (added 19 Oct 2026).
//
// CertificatePanel   - inside a programme: tick who attended, preview, issue and email certificates.
// The all-participants list lives in TrainingParticipants.js (its own sidebar tab).
//
// Backend: routes/certificates.js and the /participants routes in routes/trainings.js.
// Only attended participants can be issued a certificate; pressing Send is the human approval.
import { useState } from 'react';
import axios from 'axios';

const API = 'https://carriera-backend.onrender.com';

export const fmtDate = (d) => d ? new Date(d.length > 10 ? d : d + 'T00:00:00').toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

// PDFs need the login header, so fetch them as a file and open that. The tab is
// opened before the download starts, otherwise the browser treats it as a pop-up.
async function openPdf(url, headers) {
  const win = window.open('', '_blank');
  try {
    const res = await axios.get(url, { headers, responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    if (win) win.location.href = blobUrl; else window.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000); // free the PDF once the tab has loaded it
  } catch {
    if (win) win.close();
    alert('Could not open the certificate.');
  }
}

export function CertStatus({ cert, headers }) {
  if (!cert) return <span className="text-xs text-carriera-muted">Not issued</span>;
  return (
    <span className="text-xs text-carriera-text">
      <button onClick={() => openPdf(`${API}/api/certificates/${cert.serial}/pdf`, headers)}
        className="font-medium text-carriera-accent hover:underline">{cert.serial}</button>
      <span className="text-carriera-muted">{cert.emailed_at ? ` · emailed ${fmtDate(cert.emailed_at)}` : ' · not emailed'}</span>
    </span>
  );
}

// ── Inside a programme ─────────────────────────────
export function CertificatePanel({ registrations, setRegistrations, headers }) {
  const [saving, setSaving]       = useState(null);   // participant id being ticked
  const [confirm, setConfirm]     = useState(false);
  const [issuing, setIssuing]     = useState(false);
  const [results, setResults]     = useState(null);

  const people = registrations.flatMap(r => (r.training_participants || []).map(p => ({ ...p, company_name: r.company_name })));
  const attended = people.filter(p => p.attended);
  // Who an "issue" would touch: attended and either no certificate yet, or one that
  // was never emailed and now can be (someone with no email address isn't pending forever).
  const pending = attended.filter(p => !p.certificate || (!p.certificate.emailed_at && p.email));

  function patchParticipant(id, changes) {
    setRegistrations(prev => prev.map(r => ({
      ...r,
      training_participants: (r.training_participants || []).map(p => p.id === id ? { ...p, ...changes } : p),
    })));
  }

  async function toggleAttended(p) {
    setSaving(p.id);
    try {
      await axios.patch(`${API}/api/trainings/participants/${p.id}/attended`, { attended: !p.attended }, { headers });
      patchParticipant(p.id, { attended: !p.attended });
    } catch { alert('Failed to update attendance.'); }
    finally { setSaving(null); }
  }

  async function issue(sendEmail) {
    setIssuing(true);
    try {
      const res = await axios.post(`${API}/api/certificates/issue`,
        { participant_ids: pending.map(p => p.id), send_email: sendEmail }, { headers, timeout: 180000 });
      setResults(res.data.results);
      const now = new Date().toISOString();
      res.data.results.forEach(r => {
        if (!r.serial) return;
        const before = people.find(p => p.id === r.participant_id)?.certificate;
        patchParticipant(r.participant_id, {
          certificate: { serial: r.serial, emailed_at: r.email === 'sent' ? now : before?.emailed_at || null },
        });
      });
    } catch (err) {
      alert('Failed to issue certificates: ' + (err.response?.data?.error || err.message));
    } finally { setIssuing(false); setConfirm(false); }
  }

  if (people.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-carriera-muted uppercase tracking-widest">
          Attendance & Certificates ({attended.length} of {people.length} attended)
        </p>
        <div className="flex items-center gap-3">
          {attended[0] && (
            <button onClick={() => openPdf(`${API}/api/certificates/preview/${attended[0].id}`, headers)}
              className="text-sm text-carriera-sec border border-carriera-border px-3 py-2 hover:border-carriera-accent hover:text-carriera-accent transition-colors duration-200">
              Preview certificate
            </button>
          )}
          <button disabled={pending.length === 0} onClick={() => { setResults(null); setConfirm(true); }}
            className="bg-carriera-text text-white text-sm font-medium px-4 py-2 hover:bg-carriera-accent transition-colors duration-200 disabled:opacity-40">
            Issue certificates{pending.length ? ` (${pending.length})` : ''}
          </button>
        </div>
      </div>

      <div className="bg-white border border-carriera-border">
        {people.map((p, i) => (
          <div key={p.id} className={`flex items-center gap-4 px-4 py-3 ${i > 0 ? 'border-t border-carriera-border' : ''}`}>
            <label className="flex items-center gap-2 cursor-pointer w-28 flex-shrink-0">
              <input type="checkbox" checked={!!p.attended} disabled={saving === p.id || !!p.certificate}
                title={p.certificate ? 'Certificate already issued. Revoke it first to change attendance.' : ''}
                onChange={() => toggleAttended(p)} className="w-4 h-4 accent-carriera-accent" />
              <span className="text-xs text-carriera-sec">Attended</span>
            </label>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-carriera-text truncate">{p.full_name}</p>
              <p className="text-xs text-carriera-muted truncate">{p.company_name} · {p.email || 'no email'}</p>
            </div>
            <div className="text-right flex-shrink-0"><CertStatus cert={p.certificate} headers={headers} /></div>
          </div>
        ))}
      </div>

      {results && (
        <div className="mt-3 bg-carriera-bg border border-carriera-border p-4">
          <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">Result</p>
          {results.map(r => (
            <p key={r.participant_id} className="text-xs text-carriera-text">
              {r.name || r.participant_id}: {r.serial || ''} {r.status}{r.email ? `, email ${r.email}` : ''}{r.reason ? ` (${r.reason})` : ''}{r.error ? ` - ERROR: ${r.error}` : ''}
            </p>
          ))}
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => !issuing && setConfirm(false)}>
          <div className="bg-white w-full max-w-lg p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-xl font-normal text-carriera-text mb-1">Issue certificates</h3>
            <p className="text-sm text-carriera-muted mb-4">
              {pending.length} attended participant{pending.length !== 1 ? 's' : ''} will get a certificate.
              Check the names: they are printed exactly as below.
            </p>
            <div className="max-h-64 overflow-y-auto border border-carriera-border mb-5">
              {pending.map((p, i) => (
                <div key={p.id} className={`px-3 py-2 text-sm ${i > 0 ? 'border-t border-carriera-border' : ''}`}>
                  <span className="font-medium text-carriera-text">{p.full_name}</span>
                  <span className="text-xs text-carriera-muted"> · {p.email || 'NO EMAIL - cannot be emailed'}{p.certificate ? ` · ${p.certificate.serial}` : ''}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3">
              <button disabled={issuing} onClick={() => setConfirm(false)}
                className="text-sm text-carriera-muted hover:text-carriera-text">Cancel</button>
              <button disabled={issuing} onClick={() => issue(false)}
                className="text-sm text-carriera-sec border border-carriera-border px-3 py-2 hover:border-carriera-accent disabled:opacity-50">
                Issue only (no email)
              </button>
              <button disabled={issuing} onClick={() => issue(true)}
                className="bg-carriera-text text-white text-sm font-medium px-4 py-2 hover:bg-carriera-accent disabled:opacity-50">
                {issuing ? 'Sending...' : 'Issue & email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
